import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import {
    deleteQueryDefinitionAction,
    duplicateQueryDefinitionAction,
    exportQueryDefinitionAction,
    importQueryDefinitionAction,
} from "../actions";
import { useHistory } from "react-router-dom";
import paths from "@icarius-router/paths";
import { getLocalizedString } from "@icarius-localization/strings";
import { getAvailablePaths } from "@icarius-app/selectors";
import { customFileTypes } from "@icarius-utils/constants";

const useABM = () => {
    const dispatch = useDispatch();
    const history = useHistory();

    const availablePaths = useSelector(getAvailablePaths);
    const hasAiQueryGenerationAccess = availablePaths.includes("/aiQueryGeneration");

    const [duplicateDialogIsOpen, setDuplicateDialogIsOpen] = useState(false);
    const [deleteDialogIsOpen, setDeleteDialogIsOpen] = useState(false);
    const [createEditDialogIsOpen, setCreateEditDialogIsOpen] = useState(false);
    const [codeToDelete, setCodeToDelete] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [aiAssistedCreateDialogIsOpen, setAiAssistedCreateDialogIsOpen] = useState(false);

    const handleOpenCreateDialog = (usesAi = false) => {
        setCreateEditDialogIsOpen(true);
    };

    const handleOpenDuplicateDialog = (gridRef) => {
        //Obtener los seleccionados y armar array de files
        let selectedRows = gridRef.api.getSelectedRows();

        // Si no hay seleccionado, pido seleccionar
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectSingleItem"), severity: "warning" }));
            return;
        }

        setDuplicateDialogIsOpen(true);
        setSelectedItem(selectedRows[0]);
    };

    const handleCloseDuplicateDialog = () => {
        setDuplicateDialogIsOpen(false);
        setSelectedItem(null);
    };

    const handleOpenCreateAIQueryDialog = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();

        if (selectedRows.length !== 1 && selectedRows.length !== 0) {
            dispatch(
                openSnackbarAction({ msg: getLocalizedString("mustSelectSingleItemOrNone"), severity: "warning" })
            );
            return;
        }

        setAiAssistedCreateDialogIsOpen(true);
        setSelectedItem(selectedRows?.[0] || null);
    };

    const handleCloseAiAssistedCreateDialog = () => {
        setAiAssistedCreateDialogIsOpen(false);
    };

    const handleOpenEditDialog = (gridRef) => {
        //Obtener los seleccionados y armar array de files
        let selectedRows = gridRef.api.getSelectedRows();

        // Si no hay seleccionado, pido seleccionar
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectSingleItem"), severity: "warning" }));
            return;
        }

        setCreateEditDialogIsOpen(true);
        setSelectedItem(selectedRows[0]);
    };

    const handleCloseEditDialog = () => {
        setCreateEditDialogIsOpen(false);
        setSelectedItem(null);
    };

    const handleOpenDeleteDialog = (gridRef) => {
        //Obtener los seleccionados y armar array de files
        let selectedRows = gridRef.api.getSelectedRows();

        // Si no hay seleccionado, pido seleccionar
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectSingleItem"), severity: "warning" }));
            return;
        }

        setDeleteDialogIsOpen(true);
        setCodeToDelete(selectedRows.map((item) => item.code)[0]);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogIsOpen(false);
        setCodeToDelete(null);
    };

    const handleDelete = () => {
        dispatch(deleteQueryDefinitionAction({ code: codeToDelete })).then(() => {
            handleCloseDeleteDialog();
        });
    };

    const handleDuplicate = (data) => {
        dispatch(duplicateQueryDefinitionAction(data)).then(() => {
            handleCloseDuplicateDialog();
        });
    };

    const handleExecute = (gridRef) => {
        //Obtener los seleccionados y armar array de files
        let selectedRows = gridRef.api.getSelectedRows();

        // Si no hay seleccionado, pido seleccionar
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectSingleItem"), severity: "warning" }));
            return;
        }

        if (selectedRows[0].active !== "Y") {
            dispatch(openSnackbarAction({ msg: getLocalizedString("queryMustBeActive"), severity: "warning" }));
            return;
        }

        const group = selectedRows[0].group;
        const queryCode = selectedRows[0].code;

        history.push({
            pathname: paths.queries,
            search: `?group=${group}&queryCode=${queryCode}`,
        });
    };

    const handleExport = (gridRef) => {
        //Obtener los seleccionados y armar array de files
        let selectedRows = gridRef.api.getSelectedRows();

        // Si no hay seleccionado, pido seleccionar
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectSingleItem"), severity: "warning" }));
            return;
        }

        dispatch(exportQueryDefinitionAction({ code: selectedRows[0].code }));
    };

    const handleImport = () => {
        let input = document.createElement("input");
        input.type = "file";
        input.accept = `.${customFileTypes.icaquery}`;
        input.onchange = (e) => {
            let file = e.target.files[0];
            if (!file) return;
            dispatch(importQueryDefinitionAction(file));
        };
        input.click();
    };

    return {
        deleteDialogIsOpen,
        createEditDialogIsOpen,
        duplicateDialogIsOpen,
        selectedItem,
        handleOpenCreateDialog,
        handleOpenEditDialog,
        handleCloseEditDialog,
        handleOpenDeleteDialog,
        handleCloseDeleteDialog,
        handleOpenDuplicateDialog,
        handleCloseDuplicateDialog,
        handleDelete,
        handleDuplicate,
        handleExecute,
        handleExport,
        handleImport,
        hasAiQueryGenerationAccess,
        handleOpenCreateAIQueryDialog,
        aiAssistedCreateDialogIsOpen,
        handleCloseAiAssistedCreateDialog,
    };
};

export default useABM;
